/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SLACK ALERT SERVICE
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * Sends sync-related alerts to Slack channels
 * 
 * ALERT TYPES:
 * - New mailbox imported
 * - Sync conflict detected
 * - Activity discrepancy found
 * - Sync failure
 * 
 * USAGE:
 *   await slackAlertService.sendSyncConflictAlert(conflict)
 *   await slackAlertService.sendNewMailboxAlert(email, sequencer)
 */

import { supabase } from '@/integrations/supabase/client'
import { SyncConflict, MailboxActivityDiscrepancy, SequencerType } from './two-way-sync-types'

// ═══════════════════════════════════════════════════════════════════════════
// SLACK MESSAGE FORMATTING
// ═══════════════════════════════════════════════════════════════════════════

interface SlackBlock {
  type: string
  text?: {
    type: string
    text: string
    emoji?: boolean
  }
  elements?: any[]
  fields?: any[]
}

interface SlackMessage {
  text: string
  blocks: SlackBlock[]
  username?: string
  icon_emoji?: string
}

// ═══════════════════════════════════════════════════════════════════════════
// SLACK ALERT SERVICE
// ═══════════════════════════════════════════════════════════════════════════

class SlackAlertService {
  private webhookUrl: string | null = null

  constructor() {
    // Get Slack webhook URL from environment or settings
    this.webhookUrl = import.meta.env.VITE_SLACK_WEBHOOK_URL || null
  }

  /**
   * Send a sync conflict alert to Slack
   */
  async sendSyncConflictAlert(conflict: SyncConflict, sequencerType: SequencerType): Promise<boolean> {
    if (!this.webhookUrl) {
      console.log('⚠️ Slack webhook not configured, skipping alert')
      return false
    }

    const message: SlackMessage = {
      text: `⚠️ Sync Conflict Detected: ${conflict.email}`,
      blocks: [
        {
          type: 'header',
          text: {
            type: 'plain_text',
            text: '⚠️ Sync Conflict Detected',
            emoji: true,
          },
        },
        {
          type: 'section',
          fields: [
            {
              type: 'mrkdwn',
              text: `*Mailbox:*\n${conflict.email}`,
            },
            {
              type: 'mrkdwn',
              text: `*Sequencer:*\n${sequencerType.toUpperCase()}`,
            },
            {
              type: 'mrkdwn',
              text: `*Field:*\n${conflict.field}`,
            },
            {
              type: 'mrkdwn',
              text: `*Resolution:*\n${conflict.resolution === 'dashboard_wins' ? '✅ Dashboard value applied' : 'Manual review needed'}`,
            },
          ],
        },
        {
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: `*Values:*\n• Dashboard: \`${conflict.dashboard_value}\`\n• ${sequencerType}: \`${conflict.sequencer_value}\``,
          },
        },
        {
          type: 'context',
          elements: [
            {
              type: 'mrkdwn',
              text: `📅 Detected at: ${new Date().toLocaleString()}`,
            },
          ],
        },
      ],
      username: 'Mailbox Sync Bot',
      icon_emoji: ':warning:',
    }

    return this.sendToSlack(message)
  }

  /**
   * Send a new mailbox imported alert to Slack
   */
  async sendNewMailboxAlert(email: string, sequencerType: SequencerType, details?: any): Promise<boolean> {
    if (!this.webhookUrl) {
      console.log('⚠️ Slack webhook not configured, skipping alert')
      return false
    }

    const message: SlackMessage = {
      text: `🆕 New Mailbox Imported: ${email}`,
      blocks: [
        {
          type: 'header',
          text: {
            type: 'plain_text',
            text: '🆕 New Mailbox Auto-Imported',
            emoji: true,
          },
        },
        {
          type: 'section',
          fields: [
            {
              type: 'mrkdwn',
              text: `*Mailbox:*\n${email}`,
            },
            {
              type: 'mrkdwn',
              text: `*Source:*\n${sequencerType.toUpperCase()}`,
            },
          ],
        },
        {
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: '⚠️ *Action Required:* Please review this mailbox and complete any missing information in the dashboard.',
          },
        },
        {
          type: 'context',
          elements: [
            {
              type: 'mrkdwn',
              text: `📅 Imported at: ${new Date().toLocaleString()}`,
            },
          ],
        },
      ],
      username: 'Mailbox Sync Bot',
      icon_emoji: ':new:',
    }

    return this.sendToSlack(message)
  }

  /**
   * Send an activity discrepancy alert to Slack
   */
  async sendActivityDiscrepancyAlert(
    discrepancy: MailboxActivityDiscrepancy,
    mailboxEmail: string
  ): Promise<boolean> {
    if (!this.webhookUrl) {
      console.log('⚠️ Slack webhook not configured, skipping alert')
      return false
    }

    const emoji = discrepancy.severity === 'critical' ? '🚨' : 
                  discrepancy.severity === 'high' ? '⚠️' : 'ℹ️'

    const message: SlackMessage = {
      text: `${emoji} Activity Discrepancy: ${mailboxEmail}`,
      blocks: [
        {
          type: 'header',
          text: {
            type: 'plain_text',
            text: `${emoji} Activity Discrepancy Detected`,
            emoji: true,
          },
        },
        {
          type: 'section',
          fields: [
            {
              type: 'mrkdwn',
              text: `*Mailbox:*\n${mailboxEmail}`,
            },
            {
              type: 'mrkdwn',
              text: `*Severity:*\n${discrepancy.severity.toUpperCase()}`,
            },
            {
              type: 'mrkdwn',
              text: `*Dashboard Count:*\n${discrepancy.dashboard_sent_count}`,
            },
            {
              type: 'mrkdwn',
              text: `*${discrepancy.sequencer_type.toUpperCase()} Count:*\n${discrepancy.sequencer_sent_count}`,
            },
          ],
        },
        {
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: `*Difference:* ${discrepancy.difference} emails (${discrepancy.difference_percentage.toFixed(1)}%)`,
          },
        },
        {
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: '⚠️ *Action Required:* Please investigate this discrepancy to ensure accurate tracking.',
          },
        },
        {
          type: 'context',
          elements: [
            {
              type: 'mrkdwn',
              text: `📅 Detected at: ${new Date(discrepancy.date_detected).toLocaleString()}`,
            },
          ],
        },
      ],
      username: 'Mailbox Sync Bot',
      icon_emoji: ':chart_with_downwards_trend:',
    }

    return this.sendToSlack(message)
  }

  /**
   * Send a sync completion summary to Slack
   */
  async sendSyncSummary(
    sequencerType: SequencerType,
    mailboxesSynced: number,
    conflicts: number,
    imports: number,
    discrepancies: number,
    duration: number
  ): Promise<boolean> {
    if (!this.webhookUrl) {
      console.log('⚠️ Slack webhook not configured, skipping alert')
      return false
    }

    const hasIssues = conflicts > 0 || imports > 0 || discrepancies > 0
    const emoji = hasIssues ? '⚠️' : '✅'

    const message: SlackMessage = {
      text: `${emoji} Mailbox Sync Complete - ${sequencerType.toUpperCase()}`,
      blocks: [
        {
          type: 'header',
          text: {
            type: 'plain_text',
            text: `${emoji} Mailbox Sync Complete`,
            emoji: true,
          },
        },
        {
          type: 'section',
          fields: [
            {
              type: 'mrkdwn',
              text: `*Sequencer:*\n${sequencerType.toUpperCase()}`,
            },
            {
              type: 'mrkdwn',
              text: `*Duration:*\n${(duration / 1000).toFixed(1)}s`,
            },
            {
              type: 'mrkdwn',
              text: `*Mailboxes Synced:*\n${mailboxesSynced}`,
            },
            {
              type: 'mrkdwn',
              text: `*Conflicts:*\n${conflicts}`,
            },
            {
              type: 'mrkdwn',
              text: `*New Imports:*\n${imports}`,
            },
            {
              type: 'mrkdwn',
              text: `*Discrepancies:*\n${discrepancies}`,
            },
          ],
        },
        {
          type: 'context',
          elements: [
            {
              type: 'mrkdwn',
              text: `📅 Completed at: ${new Date().toLocaleString()}`,
            },
          ],
        },
      ],
      username: 'Mailbox Sync Bot',
      icon_emoji: hasIssues ? ':warning:' : ':white_check_mark:',
    }

    return this.sendToSlack(message)
  }

  /**
   * Send a sync failure alert to Slack
   */
  async sendSyncFailureAlert(sequencerType: SequencerType, error: string): Promise<boolean> {
    if (!this.webhookUrl) {
      console.log('⚠️ Slack webhook not configured, skipping alert')
      return false
    }

    const message: SlackMessage = {
      text: `🚨 Mailbox Sync Failed - ${sequencerType.toUpperCase()}`,
      blocks: [
        {
          type: 'header',
          text: {
            type: 'plain_text',
            text: '🚨 Mailbox Sync Failed',
            emoji: true,
          },
        },
        {
          type: 'section',
          fields: [
            {
              type: 'mrkdwn',
              text: `*Sequencer:*\n${sequencerType.toUpperCase()}`,
            },
            {
              type: 'mrkdwn',
              text: `*Status:*\nFAILED`,
            },
          ],
        },
        {
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: `*Error:*\n\`\`\`${error}\`\`\``,
          },
        },
        {
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: '🚨 *Action Required:* Please investigate the sync failure immediately.',
          },
        },
        {
          type: 'context',
          elements: [
            {
              type: 'mrkdwn',
              text: `📅 Failed at: ${new Date().toLocaleString()}`,
            },
          ],
        },
      ],
      username: 'Mailbox Sync Bot',
      icon_emoji: ':rotating_light:',
    }

    return this.sendToSlack(message)
  }

  /**
   * Send message to Slack webhook
   */
  private async sendToSlack(message: SlackMessage): Promise<boolean> {
    if (!this.webhookUrl) {
      return false
    }

    try {
      const response = await fetch(this.webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(message),
      })

      if (!response.ok) {
        console.error('Failed to send Slack message:', response.statusText)
        return false
      }

      console.log('✅ Slack alert sent successfully')
      return true

    } catch (error) {
      console.error('Error sending Slack message:', error)
      return false
    }
  }

  /**
   * Process and send alerts from the sync_alerts table
   */
  async processUnsentAlerts(): Promise<void> {
    try {
      // Fetch unsent alerts
      const { data: alerts, error } = await supabase
        .from('sync_alerts' as any)
        .select('*, mailboxes(email)')
        .eq('slack_sent', false)
        .eq('status', 'new')
        .order('created_at', { ascending: true })
        .limit(10) // Process in batches

      if (error) {
        console.error('Failed to fetch unsent alerts:', error)
        return
      }

      if (!alerts || alerts.length === 0) {
        return
      }

      console.log(`📨 Processing ${alerts.length} unsent Slack alerts...`)

      for (const alert of alerts) {
        let sent = false

        switch ((alert as any).alert_type) {
          case 'sync_conflict':
            sent = await this.sendSyncConflictAlert(
              (alert as any).details as SyncConflict,
              (alert as any).sequencer_type as SequencerType
            )
            break

          case 'new_mailbox_imported':
            const email = ((alert as any).details as any)?.email || (alert as any).mailboxes?.email || 'Unknown'
            sent = await this.sendNewMailboxAlert(
              email,
              (alert as any).sequencer_type as SequencerType
            )
            break

          case 'activity_discrepancy':
            const mailboxEmail = (alert as any).mailboxes?.email || 'Unknown'
            sent = await this.sendActivityDiscrepancyAlert(
              (alert as any).details as MailboxActivityDiscrepancy,
              mailboxEmail
            )
            break
        }

        // Mark alert as sent
        if (sent) {
          await supabase
            .from('sync_alerts' as any)
            .update({
              slack_sent: true,
              slack_sent_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            })
            .eq('id', (alert as any).id)
        }

        // Rate limiting
        await new Promise(resolve => setTimeout(resolve, 1000))
      }

      console.log(`✅ Processed ${alerts.length} alerts`)

    } catch (error) {
      console.error('Error processing unsent alerts:', error)
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// EXPORTED SERVICE INSTANCE
// ═══════════════════════════════════════════════════════════════════════════

export const slackAlertService = new SlackAlertService()






